import { ConsentPurpose } from "./enums/ConsentPurpose";

/**
 * ⚠️ TEMPLATE TEXT — REQUIRES LEGAL REVIEW BEFORE GOING LIVE.
 *
 * These are platform-standard, read-only documents that a member (or their guardian) consents
 * to. The text below is a drafted starting point, NOT reviewed legal copy — have a qualified
 * lawyer review it before relying on it in production. This is not legal advice.
 *
 * Versioning: each document carries its own `version`. The consent system pins the version a
 * member granted under (see consent-documents.ts → CURRENT_CONSENT_VERSIONS, which derives the
 * Terms/Privacy versions from this file). When a document's text changes materially, bump its
 * `version` here — existing consent records keep the version they were granted under, so history
 * stays accurate across revisions.
 */

/** A single titled block within a legal document. */
export interface LegalDocumentSection {
  heading: string;
  /** One entry per paragraph. */
  body: string[];
}

/** A platform-standard, versioned legal document shown read-only and consented to. */
export interface LegalDocument {
  purpose: ConsentPurpose;
  title: string;
  version: string;
  /** ISO date (yyyy-mm-dd) the version took effect. */
  effectiveDate: string;
  /** One-line plain-language summary shown under the title. */
  summary: string;
  sections: LegalDocumentSection[];
}

const TERMS_OF_SERVICE: LegalDocument = {
  purpose: ConsentPurpose.TermsOfService,
  title: "Terms of Service",
  version: "1.1",
  effectiveDate: "2026-11-02",
  summary:
    "The terms under which you book and attend sessions with your instructor through TimePlanned.",
  sections: [
    {
      heading: "1. About these terms",
      body: [
        "These Terms of Service govern your use of the booking and scheduling service that your instructor provides through the TimePlanned platform. By accepting these terms you agree to be bound by them.",
        "Your instructor is the person you book and attend sessions with. TimePlanned provides the software your instructor uses to manage scheduling, attendance, and payment records.",
      ],
    },
    {
      heading: "2. Booking and attending sessions",
      body: [
        "Your instructor schedules sessions and invites you to them. You are responsible for keeping your contact details accurate so you receive session information and reminders.",
        "Attendance, rescheduling, and cancellation are arranged with your instructor and are subject to any cancellation or notice policy your instructor has communicated to you.",
      ],
    },
    {
      heading: "3. Payments",
      body: [
        "Any fees for sessions are set and charged by your instructor, not by TimePlanned. Payment terms, amounts, and methods are arranged directly between you and your instructor.",
        "TimePlanned records payments your instructor enters on your behalf so that both of you have an accurate account history; it does not itself collect payment from you.",
      ],
    },
    {
      heading: "4. Acceptable use",
      body: [
        "You agree to use the service lawfully and respectfully, and not to misuse it, attempt to disrupt it, or access information that does not belong to you.",
        "TimePlanned has no tolerance for abusive, harassing or otherwise objectionable content or behavior. Anyone can report a message or a shared file from within the app or the website. TimePlanned reviews every report within 24 hours, removes content that breaks these terms, and may block the account responsible.",
      ],
    },
    {
      heading: "5. If you are booking for a child",
      body: [
        "Where the member is a minor, a parent or guardian must accept these terms on the child's behalf and acts as the contact and decision-maker for the child's bookings.",
      ],
    },
    {
      heading: "6. Liability",
      body: [
        "The service is provided on an \"as is\" basis. To the extent permitted by law, TimePlanned is not responsible for the conduct of, or the services provided by, your instructor.",
      ],
    },
    {
      heading: "7. Changes to these terms",
      body: [
        "These terms may be updated from time to time. When they change materially, a new version is published and you may be asked to accept it. The version you accepted is recorded.",
      ],
    },
    {
      heading: "8. Contact",
      body: [
        "Questions about these terms can be raised with your instructor, who can escalate platform matters to TimePlanned where relevant, or with TimePlanned directly at support@timeplanned.com.",
      ],
    },
  ],
};

const PRIVACY_POLICY: LegalDocument = {
  purpose: ConsentPurpose.PrivacyPolicy,
  title: "Privacy Policy",
  version: "1.1",
  effectiveDate: "2026-10-22",
  summary:
    "How your personal data is collected, used, and protected when you book sessions with your instructor.",
  sections: [
    {
      heading: "1. Who is responsible for your data",
      body: [
        "Your instructor decides why and how your personal data is used for managing your bookings and is the controller of that data. TimePlanned provides the platform and processes the data on your instructor's behalf.",
      ],
    },
    {
      heading: "2. What we collect",
      body: [
        "We hold the information needed to manage your sessions: your name and contact details (email and, where provided, phone), your session and attendance history, and any payment records your instructor enters.",
        "Where the member is a minor, we also hold an indication that they are a minor and the guardian who consents and acts on their behalf. We do not collect personal data we do not need for these purposes.",
      ],
    },
    {
      heading: "3. Why we use it and the legal basis",
      body: [
        "Your data is used to schedule and run your sessions, keep an accurate attendance and payment history, and contact you about your bookings. The legal basis is the performance of the arrangement between you and your instructor and, for optional uses such as marketing email, your consent.",
      ],
    },
    {
      heading: "4. Marketing communications",
      body: [
        "We only send marketing or promotional email if you have given consent for it. You can withdraw that consent at any time without affecting your bookings.",
      ],
    },
    {
      heading: "5. Who your data is shared with",
      body: [
        "Your data is accessible to your instructor and is processed by TimePlanned as the platform provider. It is not sold, and it is not shared with third parties except where needed to run the service or where required by law.",
      ],
    },
    {
      heading: "6. How long it is kept",
      body: [
        "Your data is kept for as long as you have an active relationship with your instructor and for any period afterwards required to meet legal or accounting obligations, then deleted or anonymised.",
        "Driving schools must keep a learner's training records (Ausbildungsnachweis) for five years after the end of the year in which the training ended, and then delete them (§ 31 Abs. 3 FahrlG). If you ask for your data to be erased within that time, the training record is kept until that period is over, used for no other purpose and then deleted automatically (Art. 17(3)(b) GDPR).",
      ],
    },
    {
      heading: "7. Your rights",
      body: [
        "You can ask to see the data held about you, request a copy of it, ask for it to be corrected or erased, and withdraw any consent you have given. Your instructor can action these requests through the platform's export and erasure tools.",
      ],
    },
    {
      heading: "8. Children's data",
      body: [
        "Where the member is a minor, a guardian consents on their behalf and exercises these rights for them. We treat children's data with particular care.",
      ],
    },
    {
      heading: "9. Security",
      body: [
        "We take reasonable technical and organisational measures to protect your data against loss, misuse, and unauthorised access.",
      ],
    },
    {
      heading: "10. Contact",
      body: [
        "To exercise your rights or ask about how your data is handled, contact your instructor, who can raise platform matters with TimePlanned where relevant.",
      ],
    },
  ],
};

/**
 * Platform-standard documents keyed by the consent purpose they back. Marketing-email consent
 * has no standalone document, so it is intentionally absent here.
 */
export const LEGAL_DOCUMENTS: Readonly<
  Record<ConsentPurpose.TermsOfService | ConsentPurpose.PrivacyPolicy, LegalDocument>
> = {
  [ConsentPurpose.TermsOfService]: TERMS_OF_SERVICE,
  [ConsentPurpose.PrivacyPolicy]: PRIVACY_POLICY,
};

/** The document backing a consent purpose, or undefined if that purpose has no document. */
export const getLegalDocument = (
  purpose: ConsentPurpose
): LegalDocument | undefined =>
  purpose === ConsentPurpose.TermsOfService ||
  purpose === ConsentPurpose.PrivacyPolicy
    ? LEGAL_DOCUMENTS[purpose]
    : undefined;
